import { MockedProvider } from "@apollo/client/testing";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { ContractState, ProfileType } from "../../__generated__/graphql";
import MissionTerminationScreen from "../../screens/user/MissionTermination";
import React, { useMemo } from "react";
import { GET_MISSION, GET_TERMINATION_INFO, TERMINATE_MISSION } from "../../graphql/core";
import { addDays } from "date-fns";
import { formatDateForGraphql } from "../../utils";

import {
    buildCancellationRequest,
    buildClientInfo,
    buildContract,
    buildMission,
    buildUser,
    buildWorkerInfo,
} from "../factories";
import type { Meta, StoryObj } from "@storybook/react";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

function buildMissionData(id: string, startDate: Date) {
    return buildMission({
        id: id,
        currentContract: buildContract({
            startDate: formatDateForGraphql(startDate),
            endDate: formatDateForGraphql(addDays(startDate, 50)),
        }),
    });
}

function StoryComponent({
    profileType,
    missionId,
}: {
    profileType: ProfileType;
    missionId: string;
}) {
    const user = useMemo(
        () =>
            buildUser({
                workerInfo: buildWorkerInfo(),
                clientInfos: [buildClientInfo({ id: "client" })],
            }),
        [],
    );
    const mocks = useMemo(
        () => [
            {
                variableMatcher: () => true,
                request: {
                    query: GET_MISSION,
                },
                newData: ({ id }: { id: string }) => {
                    const date =
                        id === "test-mission-id-current"
                            ? addDays(new Date(), 20)
                            : addDays(new Date(), -50);
                    return {
                        data: {
                            getMission: buildMissionData(id, date),
                        },
                    };
                },
            },
            {
                request: {
                    query: GET_TERMINATION_INFO,
                    variables: {
                        missionId: "test-mission-no_cancellation",
                    },
                },
                newData: () => ({
                    data: {
                        getTerminationInfo: {
                            newState: ContractState.Terminated,
                            minimalEndDate: formatDateForGraphql(new Date()),
                            cancellationRequest: null,
                        },
                    },
                }),
            },
            {
                request: {
                    query: GET_TERMINATION_INFO,
                    variables: {
                        missionId: "test-mission-with_cancellation-self",
                    },
                },
                newData: () => ({
                    data: {
                        getTerminationInfo: {
                            newState: ContractState.Cancelled,
                            minimalEndDate: formatDateForGraphql(new Date()),
                            cancellationRequest: buildCancellationRequest({
                                isCancelledBySelf: true,
                            }),
                        },
                    },
                }),
            },
            {
                request: {
                    query: GET_TERMINATION_INFO,
                    variables: {
                        missionId: "test-mission-with_cancellation-other",
                    },
                },
                newData: () => ({
                    data: {
                        getTerminationInfo: {
                            newState: ContractState.Cancelled,
                            minimalEndDate: formatDateForGraphql(new Date()),
                            cancellationRequest: buildCancellationRequest({
                                isCancelledBySelf: false,
                            }),
                        },
                    },
                }),
            },
            {
                variableMatcher: () => true,
                request: {
                    query: TERMINATE_MISSION,
                },
                newData: () => ({
                    data: {
                        terminateMission: {},
                    },
                }),
            },
        ],
        [],
    );
    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <MockedProvider mocks={mocks}>
                    {/* @ts-expect-error Component need a complex navigation handler */}
                    <MissionTerminationScreen route={{ params: { missionId } }} />
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
}

const meta = {
    title: "Screens/Terminate mission",
    component: StoryComponent,
    argTypes: {
        missionId: {
            options: ["test-mission-id-old", "test-mission-id-current"],
            control: { type: "radio" },
        },
        profileType: {
            options: [ProfileType.Worker, ProfileType.Client],
            control: { type: "radio" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
        missionId: "test-mission-no_cancellation",
    },
};

export const WorkerWithCancelSelf: Story = {
    args: {
        profileType: ProfileType.Worker,
        missionId: "test-mission-with_cancellation-self",
    },
};
export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
        missionId: "test-mission-no_cancellation",
    },
};

export const ClientWithCancelOther: Story = {
    args: {
        profileType: ProfileType.Client,
        missionId: "test-mission-with_cancellation-other",
    },
};
